import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  LayoutDashboard,
  User,
  FolderOpen,
  ShieldCheck,
  Receipt,
  FileText,
  Wallet,
} from 'lucide-react';
import { cn } from '../ui-x';
import { isNavItemActive } from './navItems';

/**
 * The managed-account rail. Held here rather than in navItems.js because that
 * file has no conflict — develop kept its Manage lists inside this component
 * too. `payments` owns a nested list instead of a path.
 */
const MANAGE_NAV = [
  {
    key: 'manage-dashboard',
    label: 'Dashboard',
    path: '/manage/dashboard',
    icon: LayoutDashboard,
    also: ['/manage/onboarding'],
  },
  { key: 'manage-profile', label: 'Profile', path: '/manage/profile', icon: User },
  {
    key: 'manage-documents',
    label: 'Documents',
    path: '/manage/documents',
    icon: FolderOpen,
  },
  { key: 'manage-tax', label: 'Tax', path: '/manage/tax', icon: ShieldCheck },
  {
    key: 'manage-payments',
    label: 'Payments',
    icon: Receipt,
    children: [
      { key: 'manage-invoices', label: 'Invoices', path: '/manage/invoices', icon: Receipt },
      { key: 'manage-payslips', label: 'Payslip', path: '/manage/payslips', icon: FileText },
      { key: 'manage-wallet', label: 'Wallet', path: '/manage/wallet', icon: Wallet },
    ],
  },
];

const rowClasses = (active, muted, collapsed) =>
  cn(
    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
    collapsed && 'justify-center',
    muted
      ? 'text-muted-foreground hover:bg-surface'
      : active
      ? 'bg-brand-orange-100 text-brand-orange'
      : 'text-brand-navy hover:bg-surface'
  );

/** An item that owns a nested list rather than a path (develop's "Payments"). */
const CollapsibleItem = ({ item, muted, collapsed, onNavigate, pathname }) => {
  const childActive = item.children.some((c) => isNavItemActive(c, pathname));
  const [open, setOpen] = useState(childActive);
  const Icon = item.icon;

  return (
    <div>
      <button
        type='button'
        onClick={() => setOpen((o) => !o)}
        title={collapsed ? item.label : undefined}
        className={cn(
          rowClasses(false, muted, collapsed),
          'w-full',
          !collapsed && 'justify-between'
        )}
      >
        <span className='flex items-center gap-3'>
          <Icon className={cn('h-4 w-4 shrink-0', childActive && 'text-brand-orange')} />
          {!collapsed && item.label}
        </span>
        {!collapsed && (
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-180')}
          />
        )}
      </button>

      {open && !collapsed && (
        <div className='mt-1 space-y-1 pl-4'>
          {item.children.map((child) => {
            const ChildIcon = child.icon;
            const active = isNavItemActive(child, pathname);

            return (
              <NavLink
                key={child.key}
                to={child.path}
                onClick={onNavigate}
                className={rowClasses(active, muted, false)}
              >
                <ChildIcon className={cn('h-4 w-4 shrink-0', active && 'text-brand-orange')} />
                {child.label}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * One labelled nav group, per the reference's talent rail. Rendered by the
 * desktop rail and by the mobile drawer's secondary group.
 *
 * Under /manage the rail swaps to the managed-account set: the primary group
 * renders MANAGE_NAV and the muted group is suppressed, so SideNav and
 * MobileMenu keep calling this exactly as before.
 */
const MenuItems = ({ label, items, muted = false, collapsed = false, onNavigate }) => {
  const location = useLocation();
  const isManage = location.pathname.startsWith('/manage');

  if (isManage && muted) return null;

  const groupItems = isManage ? MANAGE_NAV : items;
  const groupLabel = isManage ? 'Manage' : label;

  return (
    <div>
      {groupLabel && !collapsed && (
        <div className='px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
          {groupLabel}
        </div>
      )}
      <div className='space-y-1'>
        {groupItems.map((item) => {
          if (item.children) {
            return (
              <CollapsibleItem
                key={item.key}
                item={item}
                muted={muted}
                collapsed={collapsed}
                onNavigate={onNavigate}
                pathname={location.pathname}
              />
            );
          }

          const Icon = item.icon;
          const active = !muted && isNavItemActive(item, location.pathname);
          const className = rowClasses(active, muted, collapsed);

          const content = (
            <>
              <Icon className={cn('h-4 w-4 shrink-0', active && 'text-brand-orange')} />
              {!collapsed && item.label}
            </>
          );

          return item.external ? (
            <a
              key={item.key}
              href={item.path}
              target='_blank'
              rel='noopener noreferrer'
              className={className}
              title={collapsed ? item.label : undefined}
              onClick={onNavigate}
            >
              {content}
            </a>
          ) : (
            <NavLink
              key={item.key}
              to={item.path}
              className={className}
              title={collapsed ? item.label : undefined}
              onClick={onNavigate}
            >
              {content}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
};

export default MenuItems;